import { useCallback, useEffect } from 'react';
import { keys } from 'lodash';
import { Button } from 'tuui';
import { FormField, HelpTooltip, TextCheckbox } from 'taboola-ultimate-ui';
import { OutlineEditableText } from 'modules/campaigns/components';
import { TooltipSection } from 'modules/campaigns/modules/common-campaign-form';
import { POST_MESSAGES_TYPES } from 'modules/codeless-events/consts/iframeParentConstants';
import { GTM_EVENTS } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage, useIntl } from 'modules/taboola-common-frontend-modules/i18n';
import { ConversionCategoryField } from 'modules/tracking/components/ConversionCategoryField/ConversionCategoryField';
import { TotalValueTooltip } from 'modules/tracking/components/TotalValueCheckBox/TotalValueTooltip/TotalValueTooltip';
import { CODELESS_CATEGORIES } from 'modules/tracking/consts/CodelessCategory';
import { STEP_IDS } from '../../config/stepIds';
import { useConversionActions } from '../../hooks/useConversionActions';
import { useIncludeInTotalConversionHandler } from '../../hooks/useIncludeInTotalConversionHandler';
import { NewEventHeader } from '../Headers/NewEventHeader';
import styles from './setupURLEvent.module.scss';

export const SetupURLEvent = ({
    currentConversion,
    navigateToStepId,
    handleCancelWizard,
    isValueFeatureEnabled,
    updateDraftEvent,
    handleSaveDraftToPersistent,
}) => {
    const urlEvent = currentConversion?.urlEvent;
    const { isEventNew } = useConversionActions();

    const handleUrlEventChange = useCallback(
        value => {
            updateDraftEvent(draft => ({ ...draft, urlEvent: value }));
        },
        [updateDraftEvent]
    );

    useEffect(() => {
        if (urlEvent) {
            return;
        }
        window.parent.postMessage({ type: POST_MESSAGES_TYPES.REQUEST_URL }, '*');

        const handleUrlMessage = event => {
            if (!event.data || event.data.type !== POST_MESSAGES_TYPES.URL_RESPONSE) {
                return;
            }
            handleUrlEventChange(event.data.url);
        };

        window.addEventListener('message', handleUrlMessage);

        return () => {
            window.removeEventListener('message', handleUrlMessage);
        };
    }, [handleUrlEventChange, urlEvent]);

    const includeInTotalConversions = currentConversion?.includeInTotalConversions;
    const { handleSelectCheckbox, handleUnselectCheckbox } = useIncludeInTotalConversionHandler(updateDraftEvent);

    const handleCategoryChange = useCallback(
        categoryValue => {
            updateDraftEvent(draft => ({ ...draft, category: categoryValue.value }));
        },
        [updateDraftEvent]
    );

    const { formatMessage } = useIntl();
    const placeholder = formatMessage({
        id: `codeless.setup.url.event.placeholder`,
        defaultMessage: 'Enter the URL of the destination page',
    });

    const hasConditions = keys(currentConversion?.condition).length > 0;

    const isCategoryDisabled = !isEventNew(currentConversion.id) && hasConditions;

    const handleNextInternal = () => {
        if (isValueFeatureEnabled) {
            navigateToStepId(STEP_IDS.SETUP_VALUE);
            return;
        }
        handleSaveDraftToPersistent();
    };

    const handleBackInternal = () => {
        if (!navigateToStepId) {
            return;
        }
        if (isEventNew(currentConversion.id)) {
            navigateToStepId(STEP_IDS.CONVERSION_TYPE_SETUP);
            return;
        }
        handleCancelWizard();
        navigateToStepId(STEP_IDS.VIEW_EVENTS_LIST);
    };

    return (
        <>
            <NewEventHeader handlerArrowLeftButton={handleBackInternal} handleCancelWizard={handleCancelWizard} />
            <>
                <div className={styles['setup-title']}>
                    <FormattedMessage id="codeless..setup.url.event.header" defaultMessage="Setup URL visits event" />
                </div>
                <ConversionCategoryField
                    field="codelessDraftEvent.category"
                    isCodeless={true}
                    onChange={handleCategoryChange}
                    skipValidations={false}
                    categories={CODELESS_CATEGORIES}
                    dropdownWrapperClassName={styles['dropdown-category']}
                    disabled={isCategoryDisabled}
                />
                <FormField
                    containerClass={styles['container']}
                    inputId="urlContains"
                    label={
                        <FormattedMessage id="codeless.setup.url.event.url.contains" defaultMessage="URL contains" />
                    }
                    helpText={
                        <TooltipSection containerClass={styles['tooltip-container']} bodyClass={styles['tooltip-body']}>
                            <FormattedMessage
                                id="codeless.url.contains.tooltip"
                                defaultMessage="Enter the URL or part of the URL of the destination page on which a visit should count as a conversion."
                            />
                        </TooltipSection>
                    }
                >
                    <OutlineEditableText
                        id="urlContains"
                        className={styles['input-container']}
                        value={urlEvent ?? ''}
                        onChange={handleUrlEventChange}
                        placeholder={placeholder}
                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                        data-metrics-component="urlContains"
                    />
                </FormField>
                <div className={styles['checkbox-container']}>
                    <TextCheckbox
                        labelClassName={styles['checkbox-label']}
                        onSelectCheckbox={handleSelectCheckbox}
                        onUnselectCheckbox={handleUnselectCheckbox}
                        selectedValues={includeInTotalConversions ? [true] : []}
                        independent
                        value
                        title={
                            <FormattedMessage
                                id="tracking.conversion.properties.total.conversion.checkbox.title"
                                defaultMessage="Include in total conversions"
                            />
                        }
                        data-metrics-component="Total value checkbox"
                        data-metrics-event-name={GTM_EVENTS.USABILITY}
                        data-metrics-value={String(includeInTotalConversions)}
                    />
                    <HelpTooltip>
                        <TotalValueTooltip />
                    </HelpTooltip>
                </div>
            </>
            <footer className={styles['footer']}>
                <Button
                    onClick={handleBackInternal}
                    variant={Button.variant.outline}
                    size={Button.size.md}
                    className={styles['reset-button']}
                    aria-label="Cancel"
                >
                    <FormattedMessage id="codeless.cancel.button" defaultMessage="Cancel" />
                </Button>
                <Button
                    onClick={handleNextInternal}
                    variant={Button.variant.primary}
                    size={Button.size.md}
                    className={styles['next-button']}
                    aria-label="Next"
                    disabled={!currentConversion?.category || !urlEvent}
                >
                    <FormattedMessage id="codeless.next.button" defaultMessage="Next" />
                </Button>
            </footer>
        </>
    );
};
